import { useEffect, type ReactNode } from "react";
import { Minus, Plus, X } from "lucide-react";
import { cx } from "./api";

/** A panel that slides up from the bottom edge, the way phone apps show a detail without leaving the page. */
export function Sheet({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  return <div className="fixed inset-0 z-50 flex items-end justify-center" role="dialog" aria-modal="true" aria-label={title}>
    <button aria-label="Close" className="absolute inset-0 bg-black/70 backdrop-blur-[2px]" onClick={onClose}/>
    <div className="relative flex max-h-[92dvh] w-full max-w-lg animate-sheet flex-col rounded-t-3xl border border-b-0 border-line-strong bg-coal">
      <header className="flex items-center gap-3 border-b border-line px-5 py-4">
        <h2 className="min-w-0 flex-1 truncate text-xl">{title}</h2>
        <button onClick={onClose} aria-label="Close" className="grid size-10 shrink-0 place-items-center rounded-full bg-raised text-mute"><X size={20}/></button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
      {footer && <footer className="border-t border-line px-5 pt-3 pb-[max(0.9rem,env(safe-area-inset-bottom))]">{footer}</footer>}
    </div>
  </div>;
}

export function Stepper({ value, onChange, min = 0, max = 20, small }: { value: number; onChange: (value: number) => void; min?: number; max?: number; small?: boolean }) {
  const size = small ? "size-9" : "size-11";
  return <div className="flex items-center gap-1 rounded-full border border-line-strong bg-raised p-0.5">
    <button className={cx(size, "grid place-items-center rounded-full text-cream disabled:opacity-30")} disabled={value <= min} onClick={() => onChange(value - 1)} aria-label="One less"><Minus size={17}/></button>
    <span className="min-w-6 text-center font-bold tabular-nums" aria-live="polite">{value}</span>
    <button className={cx(size, "grid place-items-center rounded-full bg-mango text-ink disabled:opacity-30")} disabled={value >= max} onClick={() => onChange(value + 1)} aria-label="One more"><Plus size={17}/></button>
  </div>;
}
